import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LngLat, LngLatBounds, Marker, Popup } from 'mapbox-gl';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { FlagService } from '../../flag/flag.service';
import { MapboxComponent } from '../../mapbox/mapbox.component';
import { SettingService } from '../../setting/setting.service';
import { dateAsString, dateTimeAsString } from '../../shared/date.helper';
import { AnswerChange, Setting, User } from '../../shared/interfaces';
import { AddFlagComponent } from '../add.flag/add.flag.component';
import { AnswerService } from '../answer.service';
import { AnswerStatus } from '../answer.status';
import { EditFlagComponent } from '../edit.flag/edit.flag.component';
import { InspectionService } from '../inspection.service';
import { UploadImageComponent } from '../upload.image/upload.image.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-answer-form',
  templateUrl: './answer-form.component.html',
  styleUrls: ['./answer-form.component.scss'],
})
export class AnswerFormComponent implements OnInit, OnChanges {
  constructor(
    public answerService: AnswerService,
    public inspectionService: InspectionService,
    public route: ActivatedRoute,
    public router: Router,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private dialog: DialogService,
    private flagService: FlagService,
    public settingService: SettingService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {
    this.form = this.fb.group({
      answer: ['', [this.requiredConditional.bind(this)]],
    });
  }
  get answer() {
    return this._answer;
  }

  @Input()
  set answer(ans) {
    this._answer = ans;
    this.editing = false;
    if (ans && !ans.changes) {
      this.loadChanges(ans);
    }

    if (this.answer) {
      this.form.get('answer').setValue(this.answer.answer);
    } else {
      this.form.get('answer').setValue('');
    }
  }

  get ownPictures() {
    const allPictures = (this._answer && this._answer.pictures) || [];
    return allPictures ? allPictures.filter(p => !p.related) : [];
  }

  get relatedPictures() {
    const allPictures = (this._answer && this._answer.pictures) || [];
    return allPictures ? allPictures.filter(p => p.related) : [];
  }
  get editing() {
    return this._editing;
  }
  set editing(x) {
    this._editing = x;
    if (this.form) {
      if (x) {
        this.form.enable();
      } else {
        this.form.disable();
      }
    }
  }

  public user: User;

  AnswerStatus = AnswerStatus;
  dateAsString = dateAsString;
  dateTimeAsString = dateTimeAsString;

  _answer;
  changes;

  @Input() inspection;
  @Input() question;
  @Input() readOnly = false;
  @Input() showNavigation = true;
  @Output() statusChange = new EventEmitter();
  @Output() answerChange = new EventEmitter();
  @Output() setPictures = new EventEmitter();
  @Output() openPictureFlag = new EventEmitter();
  @Output() previousQuestion = new EventEmitter();
  @Output() nextQuestion = new EventEmitter();
  @Output() updateCounter = new EventEmitter<void>();

  @ViewChild('addFlagModal', { static: false }) addFlagModal: AddFlagComponent;
  @ViewChild('editFlagModal', { static: false }) editFlagModal: EditFlagComponent;
  @ViewChild('uploadImageModal', { static: false }) uploadImageModal: UploadImageComponent;

  form: UntypedFormGroup;

  _editing;

  @ViewChildren('newFlagText') private newFlagText: QueryList<ElementRef>;
  activeFlag = { id: undefined, text: '', score: 0 };

  accordion = {
    active: null,
    toggle: function (name) {
      this.active = this.active === name ? null : name;
    },
  };
  scoresEnabled = true;
  subTypeInfo: Setting[] = [];

  requiredConditional() {
    if (this.question && !this.isPictureOrLocationOnly(this.question) && this.form && !this.form.get('answer').value) {
      return { repeat: 'Field required' };
    }
  }

  async ngOnInit() {
    this.scoresEnabled = await this.settingService.scoresEnabled();
    this.subTypeInfo = await this.flagService.subtypeInfo;
  }

  async ngOnChanges(c) {
    if (this.answer && !this.answer.answer) {
      this.editing = true;
    }
    if ('answer' in c) {
      this.showMap = false;
      this.cdr.detectChanges();
      this.showMap = Array.isArray(this.answer.location);
      this.cdr.detectChanges();
    }
  }

  async loadChanges(ans) {
    try {
      ans.changes = await this.answerService.getChanges(ans.id);
    } catch (e) {
      console.error(e);
    }
  }

  async saveFlag() {
    if (!this.activeFlag.text) {
      return;
    }

    let answer;
    if (this.activeFlag.id >= 0) {
      answer = await this.flagService.edit(this.activeFlag.id, this.activeFlag);
    } else {
      answer = await this.flagService.create({
        ...this.activeFlag,
        answerId: this.answer.id,
      });
    }
    const urlParams = (this.route.params as any).value;
    this.answer.flags = answer.flags.map(f => ({
      ...f,
      deeplink: [+urlParams.item, +urlParams.section, +urlParams.question],
    }));
    this.answer.changes = answer.changes;
    this.answer.flaggingScore = answer.flaggingScore;
    this.toastr.success('Flag saved!');

    this.clearActiveFlag();
  }

  clearActiveFlag() {
    this.activeFlag = { id: undefined, text: '', score: 0 };
  }

  async editFlag(index) {
    const flag = this.answer.flags[index];

    if (flag.picture) {
      this.openPictureFlag.emit(flag);
    } else {
      this.editFlagModal.show({
        flag,
        answer: this.answer,
        // I did the update here even though editFlag function is not getting used within answer-form.component.html
        changed: newFlag => {
          const original = this.answer.flags.find(f => f.id === newFlag.id);
          original.text = newFlag.text;
          original.subtype = newFlag.subtype;
          original.subtypeId = newFlag.subtypeId;
          original.picture = newFlag.picture;
          original.pictureId = newFlag.pictureId;
          original.score = newFlag.score;
          original.groupId = newFlag.groupId;
          this.toastr.success('Flag Saved');
          this.answerChange.emit(this.answer);
        },
        deleted: () => {
          this.answer.flags.splice(
            this.answer.flags.findIndex(f => f.id === flag.id),
            1,
          );
          this.toastr.success('Flag Removed');
          this.answerChange.emit(this.answer);
          this.updateCounter.emit();
        },
      });
    }
  }

  async addComment(text) {
    if (text) {
      try {
        const answer = await this.answerService.addComment(this.answer.id, { text });
        const urlParams = (this.route.queryParams as any)._value;
        this.answer.comments = answer.comments.map(c => ({
          ...c,
          deeplink: [+urlParams.item, +urlParams.section, +urlParams.question],
        }));
        answer.changes = await this.answerService.getChanges(answer.id);
        this.answer.changes = answer.changes;
        this.updateCounter.emit();
        this.toastr.success('Comment added!');
      } catch (e) {
        const message = e.error.message;
        if (message) {
          this.toastr.error(message);
        }
      }
    }
  }

  async updateChanges() {
    this.answer.changes = (await this.answerService.get(this.answer.id)).changes;
  }

  openChangeAccordion(type: string) {
    switch (type) {
      case 'commentAdded':
      case 'commentRemoved':
        this.accordion.active = 'comments';
        break;
      case 'flagAdded':
      case 'flagRemoved':
      case 'flagChanged':
        this.accordion.active = 'flags';
        break;
      case 'answer':
      case 'accepted':
      case 'rejected':
        this.accordion.active = null;
        break;
    }
  }

  async removeComment(index) {
    this.dialog.confirm().show({
      message: `Do you really want to remove the Comment?`,
      confirmed: async () => {
        const answer = await this.answerService.removeComment(this.answer.id, index);
        const urlParams = (this.route.params as any).value;
        this.answer.comments = answer.comments.map(c => ({
          ...c,
          deeplink: [+urlParams.item, +urlParams.section, +urlParams.question],
        }));
        this.answer.changes = await this.answerService.getChanges(answer.id);
        this.updateCounter.emit();
        this.toastr.success('Comment removed!');
      },
    });
  }

  formattedDateTime(date) {
    const obj = new Date(date);
    return `${obj.getMonth() + 1}/${obj.getDate()}/${obj.getFullYear()} ${obj.getHours() < 10 ? '0' : ''}${obj.getHours()}:${obj.getMinutes() < 10 ? '0' : ''}${obj.getMinutes()}`;
  }

  accept() {
    this.statusChange.emit({ answer: this.answer, status: AnswerStatus.ACCEPTED });
    this.nextQuestion.emit();
  }

  reject() {
    this.statusChange.emit({ answer: this.answer, status: AnswerStatus.REJECTED });
  }

  showPictures() {
    this.setPictures.emit(this.answer);
  }

  getValidationClass(field: string) {
    const control = this.form.get(field);
    return {
      'is-valid': this.editing && control.valid && (control.dirty || control.touched),
      'is-invalid': this.editing && control.invalid && (control.dirty || control.touched),
    };
  }

  shouldDisableButton() {
    const hasAnswer =
      this.question && this.answer && (this.isPictureOrLocationOnly(this.question) || this.answer.answer);
    return this.readOnly || !hasAnswer;
  }

  async changeAnswer() {
    if (this.form.valid && this.answer) {
      this.answer.answer = this.form.get('answer').value;
      this.editing = false;
      const answer = await this.answerService.changeAnswer(this.answer.id, this.answer.answer);
      answer.flags = this.answer.flags;
      const urlParams = (this.route.params as any).value;
      answer.flags = (answer.flags || []).map(f => ({
        ...f,
        deeplink: [+urlParams.item, +urlParams.section, +urlParams.question],
      }));

      const changes = await this.answerService.getChanges(this.answer.id);
      // preserve related pictures
      answer.pictures = this.answer.pictures;
      answer.changes = changes;

      Object.assign(this.answer, answer);

      this.toastr.success('Answer changed!');

      // accept admin-only after saving
      if (
        this.question &&
        this.question.inspectorVisibility === 'never' &&
        this.answer.status === AnswerStatus.NOT_REVIEWED
      ) {
        this.accept();
      }

      // check if change made inspection invalid
      const inspection = await this.inspectionService.get(this.answer.inspectionId);

      if (Array.isArray(inspection.validation) && inspection.validation.length) {
        await this.dialog.alert().show({
          message: `Your change caused ${inspection.validation.length} validation problem(s) in this inspection,
                      you will be redirected to a different page where you will be able to fix it.`,
        });
        this.router.navigateByUrl(`inspection/review-invalid/${inspection.id}`);
      }
    }
  }

  rotate(orientation) {
    if (orientation) {
      switch (orientation) {
        case 3:
          return 'orientation180';

        case 6:
          return 'orientation90';

        case 8:
          return 'orientation270';
      }
    }
  }

  buildSentence(change: AnswerChange): SafeHtml {
    if (!change?.log) return '';

    let sentence = '';

    switch (change.log.type) {
      case 'answer':
        sentence = `${change.username} changed answer from "${change.log.before}" to "${change.log.after}"`;
        break;
      case 'rejected':
        sentence =
          `${change.username} Rejected the answer` +
          (change.log.isBulk ? ' <span class="changelog-bulk">Bulk</span>' : '');
        break;
      case 'unreviewed':
        sentence =
          `${change.username} Reset the answer to unreviewed` +
          (change.log.isBulk ? ' <span class="changelog-bulk">Bulk</span>' : '');
        break;
      case 'accepted':
        sentence =
          `${change.username} Accepted the answer` +
          (change.log.isBulk ? ' <span class="changelog-bulk">Bulk</span>' : '');
        break;
      case 'flagAdded':
        sentence = `${change.username} added a flag: "${change.log.text}"`;
        break;
      case 'flagRemoved':
        sentence = `${change.username} removed a flag: "${change.log.text}"`;
        break;
      case 'flagChanged': {
        const changes: string[] = [];
        if (change.log.oldScore !== change.log.newScore) {
          changes.push(`score from: "${change.log.oldScore}" to "${change.log.newScore}"`);
        }
        if (change.log.oldText !== change.log.newText) {
          changes.push(`text from: "${change.log.oldText}" to "${change.log.newText}"`);
        }
        if (change.log.oldType !== change.log.newType) {
          changes.push(`type from: "${change.log.oldType}" to "${change.log.newType}"`);
        }
        if (change.log.oldSubtype !== change.log.newSubtype) {
          const oldSubType = this.flagService.getSubtypeInfo(this.subTypeInfo, change.log.oldSubtype).name;
          const newSubType = this.flagService.getSubtypeInfo(this.subTypeInfo, change.log.newSubtype).name;
          changes.push(`subtype from: "${oldSubType}" to "${newSubType}"`);
        }
        if (change.log.oldGroup !== change.log.newGroup) {
          changes.push(`group from: "${change.log.oldGroup}" to "${change.log.newGroup}"`);
        }
        sentence = `${change.username} changed a flag ${changes.join(' and ')}`;
        break;
      }
      case 'commentAdded':
        sentence = `${change.username} added a comment: "${change.log.text}"`;
        break;
      case 'commentRemoved':
        sentence = `${change.username} removed a comment: "${change.log.text}"`;
        break;
      case 'pictureRemoved': {
        const filename = change.log.key?.split('/').pop();
        sentence = `${change.username} removed a picture: "${filename}"`;
        break;
      }
      case 'pictureAdded': {
        const filename = change.log.key?.split('/').pop();
        sentence = `${change.username} added a picture: "${filename}"`;
        break;
      }
    }
    return this.sanitizer.bypassSecurityTrustHtml(sentence);
  }

  getChangeIcon(change: AnswerChange) {
    if (change?.log) {
      return {
        'fa-pencil': change.log.type === 'answer',
        'fa-check': change.log.type === 'accepted' || change.log.type === 'pictureAdded',
        'fa-times': change.log.type === 'rejected',
        'fa-refresh': change.log.type === 'unreviewed',
        'fa-flag': change.log.type === 'flagAdded' || change.log.type === 'flagChanged',
        'fa-comment': change.log.type === 'commentAdded',
        'fa-trash': ['commentRemoved', 'flagRemoved', 'pictureRemoved'].includes(change.log.type),
      };
    }
    return {};
  }

  getFlagHighlightClass(flag) {
    const score = this.scoresEnabled ? flag.score || 0 : 0;
    return {
      red: score >= 100,
      orange: score >= 5 && score < 100,
      amber: score >= 1 && score < 5,
      editing: this.activeFlag && this.activeFlag.id === flag.id,
    };
  }

  addFlag() {
    this.accordion.active = 'flags';
    this.addFlagModal.show({
      answer: this.answer,
      confirmed: () => {
        this.toastr.success('Flag Saved');
        this.answerChange.emit(this.answer);
        this.updateCounter.emit();
      },
      canceled: () => {},
    });
  }

  isPictureOrLocationOnly(question) {
    return question && (question.type === 'picture-only' || question.type === 'location-only');
  }

  showMap = false;

  mapbox: MapboxComponent;

  setMap(mapbox: MapboxComponent) {
    this.mapbox = mapbox;
    this.afterSetMap();
  }

  afterSetMap() {
    const [lng, lat] = [this.answer.location[0], this.answer.location[1]];
    if (lng >= -180 && lng <= 180 && lat >= -180 && lat <= 180) {
      this.setMarker([lng, lat]);
    }
  }

  styleLoaded() {
    const layers = this.mapbox.map.getStyle().layers;
    const circle = layers.find(l => l.id === 'circleLayer');

    if (!circle && this.lastLocation) {
      this.setMarker();
    }
  }

  lastLocation: [number, number];

  setMarker(center?) {
    if (!this.mapbox) {
      return;
    }

    if (center) {
      this.lastLocation = center;
    }

    const popup = new Popup({ offset: 30 })
      .setHTML(`<div style="padding: 5px 20px 5px 5px">${this.lastLocation.toString()}</div>`)
      .setMaxWidth('300px');

    const marker = new Marker({ draggable: false }).setLngLat(this.lastLocation).setPopup(popup);

    this.mapbox.removeMarkers();
    this.mapbox.addMarker(marker);
    this.mapbox.jumpTo({
      zoom: 19,
      center: this.lastLocation,
    });

    this.mapbox.map.setMaxBounds(
      new LngLatBounds(
        new LngLat(this.lastLocation[0] - 0.002, this.lastLocation[1] - 0.002),
        new LngLat(this.lastLocation[0] + 0.002, this.lastLocation[1] + 0.002),
      ),
    );

    // remove old layer
    if (this.mapbox.map.getLayer('circleLayer')) {
      this.mapbox.map.removeLayer('circleLayer');
      this.mapbox.map.removeSource('circleSource');
    }
  }

  addImage() {
    this.uploadImageModal.show({
      answer: this.answer,
      finished: updatedAnswer => {
        this.toastr.success('Images Saved');
        this.answer.changes = updatedAnswer.changes;
        this.answerChange.emit(this.answer);
        this.updateCounter.emit();
      },
    });
  }
}
